import { MessengerV2 } from 'lib-shared-legacy-types';
import { useEffect, useState } from 'react';

import { useDixaMessengerApi } from 'components/settings/components/sections/dixaMessenger/api/apiProvider';

import { messengerLanguages } from '../../../settings/components/generalSettings/languageSettings/languages';
import {
  getCompanySettingsQuery,
  GetCompanySettingsQuery,
} from '../../components/shared/knowledgeBaseLinkPreview/api';

import { useElevioQuery } from './useElevioQuery';

type MessengerElevioIntegration = {
  companyId: string | undefined;
  availableLocales: { code: string; label: string }[];
};

export type ElevioAccountIntegration =
  | undefined
  | (MessengerElevioIntegration & GetCompanySettingsQuery['companySettings']);

const elevioSettingsQueryVars = {};

export function useQueryElevioIntegration(
  messengerId: string | undefined,
): ElevioAccountIntegration {
  const [elevioIntegration, setElevioIntegration] = useState<
    MessengerElevioIntegration | undefined
  >();

  const messengerApi = useDixaMessengerApi();

  useEffect(() => {
    if (!messengerId) return;

    messengerApi
      .fetch(messengerId)
      .then((messenger) => {
        setElevioIntegration({
          companyId: extractElevioCompanyId(messenger.settings),
          availableLocales: extractAvailableLocales(messenger.settings),
        });
      })
      .catch((_) => {
        setElevioIntegration(undefined);
      });
  }, [messengerApi, messengerId]);

  const { data } = useElevioQuery<GetCompanySettingsQuery>({
    query: getCompanySettingsQuery,
    variables: elevioSettingsQueryVars,
    companyId: elevioIntegration?.companyId,
  });

  return !elevioIntegration || !data
    ? undefined
    : { ...elevioIntegration, ...data.companySettings };
}

function extractAvailableLocales(
  settings: MessengerV2['settings'] | undefined,
): { code: string; label: string }[] {
  return (
    settings?.general?.availableLocales.map((locale) => ({
      code: locale,
      label:
        messengerLanguages.find(({ code }) => code === locale)?.label ?? locale,
    })) ?? []
  );
}

function extractElevioCompanyId(
  settings: MessengerV2['settings'] | undefined,
): string | undefined {
  return settings?.integrations?.elevio?.enabled === true
    ? settings?.integrations?.elevio?.companyId
    : undefined;
}
